/**
 * AdminCitiesSection - Manage city pages
 * @atomic @idempotent
 */
import { useState, useCallback, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapPinIcon, GlobeAltIcon, PencilIcon, TrashIcon, DocumentTextIcon } from '@heroicons/react/24/outline'
import { adminAPI } from '../../services/api'
import { useAsyncOperation } from '../../hooks/useAsyncOperation'
import { useAdminTableState } from '../../hooks/useAdminTableState'
import AdminTableControls from './AdminTableControls'
import { createLogger } from '../../utils/logger'
import { getInputClasses, getTextareaClasses } from '../../styles/inputStyles'

const logger = createLogger('AdminCitiesSection')

const getCityIsActive = (city) => city.isActive !== undefined ? city.isActive : city.is_active !== false
const getCityRadiusKm = (city) => city.radiusKm || city.radius_km || 50
const getCityRegionCode = (city) => city.regionCode || city.region_code || ''

const AdminCitiesSection = memo(function AdminCitiesSection({ onBack }) {
  const navigate = useNavigate()
  const { execute } = useAsyncOperation()
  const [editingCity, setEditingCity] = useState(null)
  const [editForm, setEditForm] = useState({})

  const {
    data: cities,
    loading,
    error,
    searchInput,
    pagination,
    handleSearchChange,
    handlePageChange,
    refetch,
  } = useAdminTableState(
    async (params) => {
      return execute(async () => {
        const response = await adminAPI.listCities(params)
        const list = response.data?.cities || []
        return {
          data: list,
          pagination: {
            page: Math.floor((params.offset || 0) / (params.limit || 20)) + 1,
            total: response.data?.total || 0,
            totalPages: Math.ceil((response.data?.total || 0) / (params.limit || 20)),
          },
        }
      })
    },
    { pageSize: 20 }
  )

  const startEdit = useCallback((city) => {
    setEditingCity(city.id)
    setEditForm({
      name: city.name,
      region_code: city.regionCode || city.region_code || '',
      region_name: city.regionName || city.region_name || '',
      country: city.country || 'US',
      latitude: city.latitude,
      longitude: city.longitude,
      radius_km: city.radiusKm || city.radius_km || 50,
      description: city.description || '',
      image_url: city.imageUrl || city.image_url || '',
      is_active: city.isActive !== undefined ? city.isActive : city.is_active !== false,
    })
  }, [])

  const saveEdit = useCallback(async (cityId) => {
    await execute(() => adminAPI.updateCity(cityId, editForm), {
      onSuccess: () => { setEditingCity(null); refetch() },
      onError: (err) => logger.error('Failed to save city', err),
    })
  }, [editForm, refetch, execute])

  const deleteCity = useCallback(async (cityId, name) => {
    if (!confirm(`Deactivate "${name}"? This hides the city page from public view.`)) return
    await execute(() => adminAPI.deleteCity(cityId), {
      onSuccess: () => refetch(),
      onError: (err) => logger.error('Failed to deactivate city', err),
    })
  }, [refetch, execute])

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="px-4 lg:px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <MapPinIcon className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold text-gray-900">City Pages</h2>
        </div>
        <button onClick={onBack} className="text-sm text-primary hover:text-primary-600">
          ← Back to Overview
        </button>
      </div>

      <AdminTableControls
        searchValue={searchInput}
        searchPlaceholder="Search by city name, region, or slug…"
        onSearchChange={handleSearchChange}
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalItems={pagination.total}
        itemLabel="cities"
        onPageChange={handlePageChange}
      />

      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full" />
        </div>
      )}

      {error && (
        <div className="px-6 py-4 text-sm text-red-600 bg-red-50">{error}</div>
      )}

      {!loading && !error && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Region</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Radius</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cities.map((city) => (
                <tr key={city.id} className="hover:bg-gray-50">
                  {editingCity === city.id ? (
                    <td colSpan={5} className="px-4 py-3">
                      <CityEditRow
                        form={editForm}
                        onChange={setEditForm}
                        onSave={() => saveEdit(city.id)}
                        onCancel={() => setEditingCity(null)}
                      />
                    </td>
                  ) : (
                    <>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{city.name}</div>
                        <div className="text-xs text-gray-400">{city.slug}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {getCityRegionCode(city)} · {city.country}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{getCityRadiusKm(city)} km</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCityIsActive(city) ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {getCityIsActive(city) ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => navigate(`/admin/cities/${city.id}/posts`)}
                            className="p-1.5 text-gray-400 hover:text-primary rounded"
                            title="Manage posts"
                          >
                            <DocumentTextIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => startEdit(city)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                            title="Edit city"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteCity(city.id, city.name)}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                            title="Deactivate city"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {cities.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-400">
                    <GlobeAltIcon className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    No cities found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
})

const CityEditRow = memo(function CityEditRow({ form, onChange, onSave, onCancel }) {
  const set = (key) => (e) => onChange((f) => ({ ...f, [key]: e.target.value }))
  const setNum = (key) => (e) => onChange((f) => ({ ...f, [key]: Number(e.target.value) }))
  const setCheck = (key) => (e) => onChange((f) => ({ ...f, [key]: e.target.checked }))

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <input value={form.name} onChange={set('name')} placeholder="Name" className={`col-span-2 ${getInputClasses(false)}`} />
      <input value={form.region_code} onChange={set('region_code')} placeholder="Region code (TX)" className={getInputClasses(false)} />
      <input value={form.country} onChange={set('country')} placeholder="Country (US)" className={getInputClasses(false)} />
      <input value={form.latitude} onChange={set('latitude')} placeholder="Latitude" type="number" step="0.000001" className={getInputClasses(false)} />
      <input value={form.longitude} onChange={set('longitude')} placeholder="Longitude" type="number" step="0.000001" className={getInputClasses(false)} />
      <input value={form.radius_km} onChange={setNum('radius_km')} placeholder="Radius km" type="number" className={getInputClasses(false)} />
      <label className="flex items-center gap-1.5 text-sm text-gray-600">
        <input type="checkbox" checked={form.is_active} onChange={setCheck('is_active')} className="rounded border-gray-300" />
        Active
      </label>
      <div className="col-span-2">
        <input value={form.image_url} onChange={set('image_url')} placeholder="Image URL (optional)" className={getInputClasses(false)} />
      </div>
      <textarea value={form.description} onChange={set('description')} placeholder="Description (optional)" rows={2} className={`col-span-2 sm:col-span-4 ${getTextareaClasses()}`} />
      <div className="col-span-2 sm:col-span-4 flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
        <button onClick={onSave} className="px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-600">Save</button>
      </div>
    </div>
  )
})

export default AdminCitiesSection
